import type { Auth, UserCredential } from "firebase/auth";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import { signIn } from "next-auth/react";

type OAuthProviderId = "apple.com" | "microsoft.com";

interface WebAuthBridge {
  start: (opts: {
    url: string;
    callbackScheme: string;
  }) => Promise<{ url: string }>;
}

// crypto.randomUUID requires a secure context, which http://10.0.2.2:3000
// (the Android emulator's host-loopback dev URL) is not. The eventId only
// needs to be unique per request, not cryptographically strong.
function generateEventId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function extractIdTokenFromCallback(callbackUrl: string): string | undefined {
  const outer = new URL(callbackUrl);
  const linkStr = outer.searchParams.get("link") || callbackUrl;
  const inner = new URL(linkStr);
  return (
    inner.searchParams.get("id_token") ||
    inner.hash
      .substring(1)
      .split("&")
      .find((p) => p.startsWith("id_token="))
      ?.split("=")[1]
  );
}

/**
 * Shared flow for OAuth providers that route through Firebase's
 * /__/auth/handler: open the handler URL in the native browser (iOS
 * ASWebAuthenticationSession or Android Chrome Custom Tabs), capture the
 * provider's id_token from the deep-link callback, and exchange it with
 * Firebase for a user credential. Used by Microsoft on both platforms and
 * by Apple on Android (iOS uses the native Apple Sign In SDK instead).
 */
async function webAuthFirebaseSignIn(
  firebaseAuth: Auth,
  opts: {
    providerId: OAuthProviderId;
    scopes: string;
    customParameters?: Record<string, string>;
  },
): Promise<UserCredential> {
  const { registerPlugin } = await import("@capacitor/core");
  const webAuth = registerPlugin<WebAuthBridge>("WebAuth");

  const authDomain = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN;
  const apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
  // Match the deep-link scheme registered by the running APK. Android builds
  // now use a per-env applicationId (com.medly.medly{.dev,.staging,<none>})
  // and the intent-filter is scheme="${applicationId}", so the callback only
  // reaches this process when we echo the same value back to Firebase.
  // MainActivity injects window.__APP_PACKAGE__ at page load on Android;
  // iOS keeps the single bundle id com.medly.medly so the fallback is safe.
  const callbackScheme =
    (globalThis as { __APP_PACKAGE__?: string }).__APP_PACKAGE__ ??
    "com.medly.medly";
  const redirectUrl = `https://${authDomain}/__/auth/handler`;

  const customParamsSegment = opts.customParameters
    ? `&customParameters=${encodeURIComponent(JSON.stringify(opts.customParameters))}`
    : "";

  const url =
    `https://${authDomain}/__/auth/handler?apiKey=${apiKey}` +
    `&authType=signInViaRedirect&providerId=${opts.providerId}` +
    `&scopes=${encodeURIComponent(opts.scopes)}${customParamsSegment}` +
    `&appName=%5BDEFAULT%5D&redirectUrl=${encodeURIComponent(redirectUrl)}` +
    `&eventId=${generateEventId()}`;

  const result = await webAuth.start({ url, callbackScheme });
  const idToken = extractIdTokenFromCallback(result.url);
  if (!idToken) {
    throw new Error(`No id_token returned from ${opts.providerId} sign-in`);
  }

  const credential = new OAuthProvider(opts.providerId).credential({ idToken });
  return signInWithCredential(firebaseAuth, credential);
}

/**
 * iOS-only: direct OAuth against login.microsoftonline.com, bypassing
 * Firebase's /__/auth/handler. The handler relies on sessionStorage
 * written by signInWithRedirect on the calling origin, which doesn't
 * carry into ASWebAuthenticationSession's isolated session — causing
 * "Unable to process request due to missing initial state". Instead
 * we request an id_token directly, parse it from the callback fragment,
 * and exchange it with Firebase via signInWithCredential.
 *
 * Azure AD app client IDs are per-env and injected as window.__MICROSOFT_CLIENT_ID__
 * by MedlyViewController (from capacitor.config.json → APP_ENV map).
 * Redirect URI msauth.com.medly.medly://auth matches what the Azure app
 * registrations accept under "Mobile and desktop applications".
 */
async function iosNativeMicrosoftSignIn(
  firebaseAuth: Auth,
): Promise<UserCredential> {
  const w = window as unknown as Record<string, string | undefined>;
  const clientId =
    w.__MICROSOFT_CLIENT_ID__ ?? process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID;
  if (!clientId) throw new Error("Microsoft client ID not available");

  const { registerPlugin } = await import("@capacitor/core");
  const webAuth = registerPlugin<WebAuthBridge>("WebAuth");

  const redirectUri = "msauth.com.medly.medly://auth";
  const callbackScheme = "msauth.com.medly.medly";
  const nonce = generateEventId();

  const url =
    `https://login.microsoftonline.com/common/oauth2/v2.0/authorize` +
    `?client_id=${encodeURIComponent(clientId)}` +
    `&response_type=id_token` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_mode=fragment` +
    `&scope=${encodeURIComponent("openid profile email")}` +
    `&nonce=${encodeURIComponent(nonce)}` +
    `&prompt=select_account`;

  const result = await webAuth.start({ url, callbackScheme });

  const hashIndex = result.url.indexOf("#");
  const hash = hashIndex === -1 ? "" : result.url.slice(hashIndex + 1);
  const idToken = new URLSearchParams(hash).get("id_token");
  if (!idToken) {
    throw new Error("No id_token returned from Microsoft sign-in");
  }

  const credential = new OAuthProvider("microsoft.com").credential({
    idToken,
    rawNonce: nonce,
  });
  return signInWithCredential(firebaseAuth, credential);
}

async function iosNativeAppleSignIn(
  firebaseAuth: Auth,
): Promise<UserCredential> {
  const { SignInWithApple } =
    await import("@capacitor-community/apple-sign-in");
  const appleResult = await SignInWithApple.authorize({
    clientId: "com.medly.medly",
    scopes: "email name",
    redirectURI: "",
    nonce: "",
  });
  const credential = new OAuthProvider("apple.com").credential({
    idToken: appleResult.response.identityToken,
  });
  return signInWithCredential(firebaseAuth, credential);
}

// Re-export the canonical implementation so existing imports from
// "@medly/auth-config/native-sign-in" keep working. Single source of truth
// lives in product-cookie.ts.
export { isCapacitorNative } from "./product-cookie";

/**
 * iOS: MedlyViewController injects __GOOGLE_IOS_CLIENT_ID__ (from the plist)
 * and __GOOGLE_WEB_CLIENT_ID__ (from capacitor.config.json, sourced from
 * GOOGLE_WEB_CLIENT_ID at cap-sync time). GoogleSignIn 9.x needs
 * iOSServerClientId = webClientId so the idToken audience matches Firebase.
 * See Cap-go/capacitor-social-login#159.
 * Android: MainActivity injects __GOOGLE_WEB_CLIENT_ID__ from
 * google-services.json's default_web_client_id. No iOS client needed.
 */
async function initGoogleSocialLogin() {
  const { Capacitor } = await import("@capacitor/core");
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  const w = window as unknown as Record<string, string | undefined>;
  const webClientId =
    w.__GOOGLE_WEB_CLIENT_ID__ ?? process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID;
  if (!webClientId) throw new Error("Google web client ID not available");

  if (Capacitor.getPlatform() === "ios") {
    const iOSClientId =
      w.__GOOGLE_IOS_CLIENT_ID__ ??
      process.env.NEXT_PUBLIC_GOOGLE_IOS_CLIENT_ID;
    if (!iOSClientId) throw new Error("Google iOS client ID not available");
    await SocialLogin.initialize({
      google: {
        iOSClientId,
        iOSServerClientId: webClientId,
        webClientId,
        mode: "online",
      },
    });
  } else {
    await SocialLogin.initialize({
      google: { webClientId, mode: "online" },
    });
  }
  // Wrap the plugin in a plain object — the Capacitor plugin proxy is
  // a thenable (its Proxy handler returns a function for ANY prop access,
  // including "then"), so returning it directly would make `await` on the
  // enclosing Promise try to unwrap SocialLogin itself, calling .then() and
  // triggering a "SocialLogin.then() is not implemented" error on Android.
  return { SocialLogin };
}

/**
 * Creates native sign-in helpers that stop at the Firebase client credential
 * — no NextAuth exchange. Used by apps on Firebase-first sessions (exams),
 * which establish their own session cookie from the returned credential.
 */
export function createNativeCredentialHelpers(firebaseAuth: Auth) {
  /**
   * Sign in with Apple. iOS uses the native Apple Sign In sheet (via
   * @capacitor-community/apple-sign-in), Android routes through Firebase's
   * /__/auth/handler over Chrome Custom Tabs because Apple's native SDK is
   * iOS-only and the community plugin is a stub on Android.
   */
  const nativeAppleCredential = async (): Promise<UserCredential> => {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.getPlatform() === "ios"
      ? iosNativeAppleSignIn(firebaseAuth)
      : webAuthFirebaseSignIn(firebaseAuth, {
          providerId: "apple.com",
          scopes: "email name",
        });
  };

  /**
   * Sign in with Microsoft. iOS hits login.microsoftonline.com directly
   * (iosNativeMicrosoftSignIn) because Firebase's /__/auth/handler fails
   * under ASWebAuthenticationSession's isolated sessionStorage. Android
   * routes through the Firebase handler over Chrome Custom Tabs, which
   * works because the handler can read and write its own storage there.
   */
  const nativeMicrosoftCredential = async (): Promise<UserCredential> => {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.getPlatform() === "ios"
      ? iosNativeMicrosoftSignIn(firebaseAuth)
      : webAuthFirebaseSignIn(firebaseAuth, {
          providerId: "microsoft.com",
          scopes: "email,profile",
          customParameters: { prompt: "select_account" },
        });
  };

  /**
   * Sign in with Google using the native SDK (modal sheet).
   *
   * Uses @capgo/capacitor-social-login (a maintained fork of the abandoned
   * @codetrix-studio/capacitor-google-auth). The Capgo plugin pulls in
   * GoogleSignIn 7.x and GTMAppAuth/GTMSessionFetcher versions that ship
   * privacy manifests, which is required by Apple as of May 2024
   * (ITMS-91061).
   */
  const nativeGoogleCredential = async (): Promise<UserCredential> => {
    const { SocialLogin } = await initGoogleSocialLogin();
    // Clear any cached session from a different Firebase project.
    // Capgo's logout requires a provider arg.
    await SocialLogin.logout({ provider: "google" }).catch(() => {});
    // Don't pass `scopes` — Capgo already requests email/profile/openid by
    // default on both platforms, and passing an explicit scopes array on
    // Android requires the app's MainActivity to implement Capgo's
    // ModifiedMainActivityForSocialLoginPlugin interface (plugin rejects the
    // call otherwise). See GoogleProvider.java:313 in the Capgo source.
    const { result } = await SocialLogin.login({
      provider: "google",
      options: {},
    });
    if (result.responseType !== "online") {
      throw new Error(
        `Unexpected Google login response type: ${result.responseType}`,
      );
    }
    if (!result.idToken) {
      throw new Error("Google login returned no idToken");
    }
    const credential = GoogleAuthProvider.credential(result.idToken);
    return signInWithCredential(firebaseAuth, credential);
  };

  return {
    nativeAppleCredential,
    nativeGoogleCredential,
    nativeMicrosoftCredential,
  };
}

/**
 * Creates native social sign-in helpers bound to an app's Firebase auth
 * instance, so web apps can share one Capacitor auth implementation.
 * Each helper performs the Firebase credential sign-in and then exchanges
 * it for a NextAuth session; returns the NextAuth sign-in result.
 */
export function createNativeSignInHelpers(firebaseAuth: Auth) {
  const {
    nativeAppleCredential,
    nativeGoogleCredential,
    nativeMicrosoftCredential,
  } = createNativeCredentialHelpers(firebaseAuth);

  const exchangeForNextAuthSession = async (
    providerFlag: Record<string, string>,
    firebaseResult: UserCredential,
  ) => {
    const id_token = await firebaseResult.user.getIdToken();
    return signIn("credentials", {
      ...providerFlag,
      id_token,
      firebase_refresh_token: firebaseResult.user.refreshToken,
      redirect: false,
    });
  };

  return {
    nativeAppleSignIn: async () =>
      exchangeForNextAuthSession(
        { isAppleSignIn: "true" },
        await nativeAppleCredential(),
      ),
    nativeGoogleSignIn: async () =>
      exchangeForNextAuthSession(
        { isGoogleSignIn: "true" },
        await nativeGoogleCredential(),
      ),
    nativeMicrosoftSignIn: async () =>
      exchangeForNextAuthSession(
        { isMicrosoftSignIn: "true" },
        await nativeMicrosoftCredential(),
      ),
  };
}
